import { Link, useLocation } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { navGroups, isActive } from '@/lib/nav';
import { FallbackIcon, navIcons } from './navIcons';
import { Button } from '@/components/ui/button';

const VERSION = import.meta.env.VITE_APP_VERSION || 'dev';

export interface SidebarProps {
  collapsed: boolean;
  onToggle?: () => void;
  onNavigate?: () => void;
  className?: string;
}

export function Sidebar({ collapsed, onToggle, onNavigate, className }: SidebarProps) {
  const { pathname } = useLocation();
  return (
    <aside className={cn('flex h-full flex-col overflow-hidden border-r bg-sidebar text-sidebar-foreground transition-[width] duration-200', collapsed ? 'w-16' : 'w-64', className)}>
      <div className="flex h-14 shrink-0 items-center justify-between gap-2 border-b px-3">
        {!collapsed && (
          <div className="min-w-0 truncate whitespace-nowrap pl-1">
            <span className="font-semibold tracking-tight">Portainer Bridge</span>
            <span className="ml-1.5 font-mono text-xs text-muted-foreground">{VERSION}</span>
          </div>
        )}
        {onToggle && (
          <Button variant="ghost" size="icon" onClick={onToggle} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} className={cn('shrink-0', collapsed && 'mx-auto')}>
            {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
          </Button>
        )}
      </div>
      <nav className={cn('flex-1 overflow-y-auto overflow-x-hidden p-3', collapsed ? 'space-y-3' : 'space-y-5')} aria-label="Main">
        {navGroups.map((group, gi) => (
          <div key={group.label} className="space-y-1">
            {collapsed
              ? gi > 0 && <div className="mx-2 border-t" aria-hidden />
              : <p className="whitespace-nowrap px-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground" aria-hidden>{group.label}</p>}
            <ul aria-label={group.label} className="space-y-1">
              {group.items.map((item) => {
                const Icon = navIcons[item.icon] ?? FallbackIcon;
                const active = isActive(item, pathname);
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={onNavigate}
                      title={collapsed ? item.label : undefined}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'relative flex h-9 items-center gap-3 whitespace-nowrap rounded-md px-2.5 text-sm font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring',
                        collapsed && 'justify-center px-0',
                        active ? 'bg-sidebar-accent text-sidebar-accent-foreground' : 'text-muted-foreground hover:bg-sidebar-accent/60 hover:text-foreground',
                      )}
                    >
                      {active && <span className="absolute inset-y-1.5 left-0 w-0.5 rounded-full bg-primary" aria-hidden />}
                      <Icon className="size-4 shrink-0" aria-hidden />
                      {collapsed ? <span className="sr-only">{item.label}</span> : <span className="truncate">{item.label}</span>}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
}
